// ============================================================================
// Envoi des leads vers le webhook n8n — en 2 temps.
//   Le POST part vers /api/lead (route serveur) qui relaie vers LEAD_WEBHOOK_URL,
//   de sorte que l'URL du webhook n'est jamais exposée au client.
//
//   • partial_submission — dès que le contact (nom / email / tél) est validé,
//     avant le QCM (le lead est capturé au plus tôt, même s'il abandonne).
//   • full_submission    — à la fin du QCM, avec les 3 réponses + qualification.
//
//   Payload (aligné sur le format attendu par n8n) :
//     { name, mail, num, company, Q1, Q2, Q3, type, ...utms, metadata }
// ============================================================================

import {
  FormData,
  Option,
  MATURITE_IA_OPTIONS,
  STATUT_OPTIONS,
  TAILLE_ENTREPRISE_OPTIONS,
  isQualified,
} from "./form";
import { getStoredUtms } from "./utm";

export type LeadStage = "partial_submission" | "full_submission";

const FORM_NAME = "Bootcamp Manager Augmenté";

// Libellés des 3 questions du QCM (ordre d'affichage = Q1, Q2, Q3).
const Q1_LABEL = "Maturité IA";
const Q2_LABEL = "Situation";
const Q3_LABEL = "Taille entreprise";

// value technique -> libellé lisible (celui affiché à l'utilisateur).
const labelOf = (options: Option[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

function buildPayload(stage: LeadStage, data: FormData): Record<string, unknown> {
  const utms = getStoredUtms();

  const metadata: Record<string, unknown> = {
    form_name: FORM_NAME,
    is_company_bool: false,
    submitted_at: new Date().toISOString(),
  };

  const payload: Record<string, unknown> = {
    name: data.nomComplet,
    mail: data.email,
    num: data.phone,
    company: "",
    type: stage,
    ...utms,
    metadata,
  };

  if (stage === "full_submission") {
    payload.Q1 = labelOf(MATURITE_IA_OPTIONS, data.maturiteIA);
    payload.Q2 = labelOf(STATUT_OPTIONS, data.statut);
    payload.Q3 = labelOf(TAILLE_ENTREPRISE_OPTIONS, data.tailleEntreprise);
    metadata.q1_label = Q1_LABEL;
    metadata.q2_label = Q2_LABEL;
    metadata.q3_label = Q3_LABEL;
    metadata.qualified = isQualified(data);
  }

  return payload;
}

/**
 * Envoie le lead au webhook (fire-and-forget : n'interrompt jamais le funnel,
 * même en cas d'échec réseau). `keepalive` permet au POST de survivre à une
 * navigation immédiate (ex. redirection vers /merci).
 */
export function sendLead(stage: LeadStage, data: FormData): void {
  if (typeof window === "undefined") return;
  try {
    fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildPayload(stage, data)),
      keepalive: true,
    }).catch(() => {
      /* échec silencieux : ne jamais bloquer l'expérience */
    });
  } catch {
    /* idem — le funnel prime sur l'envoi */
  }
}
